/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { If, Then, Else, When } from "react-if";
import { theme, IconBox, Tag, Text } from "@nordcloud/gnui";
import { QueryStateFilterValue } from "~/hooks";
import { isNotNil, isNotEmpty } from "~/tools";
import { UniversalWrap } from "../Utils";
import { useGetDataByIds } from "./hooks";
import {
  OptionsWrapper,
  StyledOptions,
  StyledOption,
  OptionIcon,
} from "./styles";
import { getLabel, getColor } from "./utils";

type Props = {
  name: string;
  value: QueryStateFilterValue;
  onRemoveKey: () => void;
  onRemoveValue: (key: string, index: number) => void;
};

type OptionType = {
  name: string;
  nid: string;
};

export function ExpandableTag({
  name,
  value,
  onRemoveKey,
  onRemoveValue,
}: Props) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isHovered, setHovered] = React.useState(false);

  const valueArray = Array.isArray(value)
    ? value.map((item) => item.toString())
    : [];

  const { loading, error, data } = useGetDataByIds({
    name,
    value: valueArray,
  });

  const onRemoveValueHandler = (inputName: string, index: number) => {
    setHovered(false);
    onRemoveValue(inputName, index);
  };

  const mappedData = valueArray
    .map((element) => data.find((d) => d.nid === element))
    .filter(isNotNil);

  return (
    <OptionsWrapper
      ref={wrapperRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <UniversalWrap
        loaderProps={{
          loading,
        }}
        errorProps={{
          error: undefined,
        }}
      >
        {isHovered && (
          <If condition={isNotEmpty(mappedData)}>
            <Then>
              <StyledOptions>
                {mappedData?.map(
                  ({ nid, name: itemName }: OptionType, index: number) => (
                    <StyledOption
                      key={nid}
                      justifyContent="space-between"
                      onClick={() => onRemoveValueHandler(name, index)}
                    >
                      <Text
                        nowrap
                        size="md"
                        mb="0"
                        mr={theme.spacing.spacing04}
                      >
                        {itemName}
                      </Text>
                      <IconBox css={{ marginRight: 0, flexShrink: 0 }}>
                        <OptionIcon name="close" />
                      </IconBox>
                    </StyledOption>
                  )
                )}
              </StyledOptions>
            </Then>
            <Else>
              <When condition={isNotEmpty(valueArray) && !error}>
                <StyledOptions>
                  {valueArray.map((item, index) => (
                    <StyledOption
                      key={item.toString()}
                      justifyContent="space-between"
                      onClick={() => onRemoveValueHandler(name, index)}
                    >
                      <Text
                        nowrap
                        size="md"
                        mb="0"
                        mr={theme.spacing.spacing03}
                      >
                        {item}
                      </Text>
                      <IconBox css={{ marginRight: 0 }}>
                        <OptionIcon name="close" />
                      </IconBox>
                    </StyledOption>
                  ))}
                </StyledOptions>
              </When>
            </Else>
          </If>
        )}
      </UniversalWrap>
      <Tag
        showClose
        text={getLabel(name, value)}
        color={getColor(name)}
        css={{
          margin: theme.spacing.spacing02,
          marginLeft: 0,
        }}
        onClick={onRemoveKey}
      />
    </OptionsWrapper>
  );
}
